import { FC } from "react";
import { Modal, Table, Spin, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useActiveCreditLots } from "hooks/useFactoryCredit";
import { ActiveCreditLotRow } from "services/factoryCreditApi";
import { numberFormatInt } from "utils/nuberFormat";

const { Text } = Typography;

const daysRemainingLabel = (days: number): string => (days <= 0 ? "วันนี้" : `${days} วัน`);

// ลำดับ column ตามที่ตกลง (2026-08-26): จำนวนเครดิตก่อน แล้วค่อยหมดอายุภายใน (วัน) — เพราะ "เท่าไหร่"
// สำคัญกับผู้ใช้มากกว่า "อีกกี่วัน" เวลากวาดสายตาจากซ้ายไปขวา
const columns: ColumnsType<ActiveCreditLotRow> = [
  {
    title: "ลำดับที่",
    key: "no",
    align: "center",
    width: 64,
    render: (_: unknown, __: ActiveCreditLotRow, index: number) => index + 1,
  },
  {
    title: "จำนวนเครดิต",
    dataIndex: "total_display",
    key: "total_display",
    align: "right",
    render: (v: number) => numberFormatInt(v),
  },
  {
    title: "หมดอายุภายใน (วัน)",
    dataIndex: "days_remaining",
    key: "days_remaining",
    align: "right",
    render: (days: number, row: ActiveCreditLotRow) => (
      // TEMP FOR TESTING (2026-08-26): โชว์วันที่จริงในวงเล็บต่อท้ายด้วย เพื่อเทียบเลขวันคงเหลือ
      // กับวันที่จริงตอนทดสอบ — ทดสอบเสร็จแล้วให้เอาวงเล็บนี้ออก (กลับไปเหลือแค่ daysRemainingLabel)
      <span>
        {daysRemainingLabel(days)} ({row.date_label})
      </span>
    ),
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Modal รายละเอียด "เครดิตกำลังหมดอายุ" — โชว์เครดิต active ทุกก้อน รวมตามวันปฏิทินไทยที่หมดอายุ
 * เรียงใกล้หมดอายุสุดก่อน ใช้ร่วมกันทั้งหน้าคำสั่งซื้อ (COD) และหน้าส่งพัสดุนอก SKU เพราะ credit pool
 * เดียวกัน (ดู OrderCodCreditCard.tsx / ParcelCreditCard.tsx)
 *
 * 2026-08-26: ย้ายการ group ตามวันปฏิทิน + คำนวณ "จำนวนวันคงเหลือ" ไปทำที่ backend ทั้งหมดแล้ว (เดิม
 * ทำที่นี่ด้วย client clock — เสี่ยงผิดถ้านาฬิกาเครื่อง user เพี้ยน) component นี้เหลือแค่ render ข้อมูล
 * ที่ backend ส่งมาสำเร็จรูป ดู docstring ของ FactoryCreditService.getActiveCreditLots ฝั่ง API
 *
 * ย้าย "เครดิตรวม" จาก footer ท้ายตาราง (Table.Summary) ขึ้นมาเป็น banner เด่นๆใต้ title แทน — เพราะ
 * "มีเท่าไหร่รวม" คือคำถามแรกที่คนเปิด modal นี้อยากรู้ ควรเห็นทันทีไม่ต้องเลื่อนตารางไปดูท้ายสุด
 * ส่วนตารางด้านล่างคือรายละเอียดสนับสนุน (breakdown ตามวันหมดอายุ)
 */
const ExpiringCreditModal: FC<Props> = ({ open, onClose }) => {
  const { data, isLoading } = useActiveCreditLots(open);
  const rows = data?.rows ?? [];
  const totalDisplay = data?.total_display ?? 0;

  return (
    <Modal open={open} title="เครดิตที่ใช้ได้" onCancel={onClose} footer={null} centered>
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <Spin size="small" />
        </div>
      ) : rows.length === 0 ? (
        <Text type="secondary">ไม่พบข้อมูลเครดิตที่ใช้ได้</Text>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              marginBottom: 16,
              background: "#f0f5ff",
              border: "1px solid #adc6ff",
              borderRadius: 8,
            }}
          >
            <Text style={{ fontSize: 14, color: "#333" }}>เครดิตคงเหลือทั้งหมด</Text>
            <Text strong style={{ fontSize: 22, color: "#1890ff", lineHeight: 1.2 }}>
              {numberFormatInt(totalDisplay)}
            </Text>
          </div>

          <Table<ActiveCreditLotRow>
            rowKey="date_label"
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={false}
            // สลับสีแถว (ตาราง zebra) — ใช้ inline style ผ่าน onRow แทน rowClassName + CSS global
            // เพราะเป็นตารางเดียว ไม่ต้องเพิ่ม stylesheet/class ที่อาจไปชนกับตารางอื่นในระบบ
            onRow={(_, index) => ({
              style: (index ?? 0) % 2 === 1 ? { background: "#fafafa" } : undefined,
            })}
          />
        </>
      )}
    </Modal>
  );
};

export default ExpiringCreditModal;
